// The tray: a row of tabs with one panel shown at a time. Only the compact
// layout has one. It is under the board and holds every part. The wide
// layout has no tabs: the log is always shown and the card rests on top of it.
// The tray follows the game: the spell when the card lands, the piece when
// the player picks one, and the log when the turn of the card is over. A tap
// on a tab always wins until the next of those moments. The wheel is not a
// tab. It is a row over the tray that shows only at a roll.

import { isCardHeld } from '../game/flow';
import { createStore } from '../lib/store';
import { gameStore } from './game';
import { type LayoutMode, layoutStore } from './layout';
import { stageStore } from './stage';
import { uiStore } from './ui';
import { setWheelPick, wheelPickStore } from './wheelPick';

export const TRAY_TABS = ['spell', 'piece', 'log', 'match'] as const;

export type TrayTab = (typeof TRAY_TABS)[number];

/** The tabs each layout shows, in order. */
export const COMPACT_TRAY_TABS = TRAY_TABS;

const LAYOUT_TABS: Record<LayoutMode, readonly TrayTab[]> = {
  wide: [],
  compact: COMPACT_TRAY_TABS,
};

/** The tab a layout shows when no card is held. */
const REST_TAB: Record<LayoutMode, TrayTab> = { wide: 'log', compact: 'log' };

const holdsCard = (): boolean => isCardHeld(gameStore.get());

const tabAtRest = (): TrayTab => (holdsCard() ? 'spell' : REST_TAB[layoutStore.get()]);

export const trayStore = createStore<TrayTab>(REST_TAB[layoutStore.get()]);

export const showTrayTab = (tab: TrayTab): void => {
  if (!LAYOUT_TABS[layoutStore.get()].includes(tab)) return;
  // The picked face belongs to the Spell tab. It goes when the tab does.
  if (tab !== 'spell') setWheelPick(null);
  if (trayStore.get() !== tab) trayStore.set(tab);
};

/** Puts the open face of the wheel back. The hand shows the held card again. */
export const closeWheelFace = (): void => {
  setWheelPick(null);
  showTrayTab(tabAtRest());
};

/**
 * Shows a face of the wheel as a card where the hand is: on top of the log in the wide layout.
 * A second pick of the same face puts it back.
 */
export const showWheelFace = (face: number): void => {
  if (wheelPickStore.get() === face) {
    closeWheelFace();
    return;
  }
  setWheelPick(face);
  showTrayTab('spell');
};

/** Call once at startup, after the match loads. */
export const startTray = (): void => {
  showTrayTab(tabAtRest());
  layoutStore.subscribe(() => {
    setWheelPick(null);
    trayStore.set(tabAtRest());
  });
  stageStore.subscribe((stage) => {
    if (stage.phase === 'leaving') {
      // The card that leaves the stage is the one to read now.
      setWheelPick(null);
      showTrayTab('spell');
    }
  });
  uiStore.subscribe((ui) => {
    if (ui.selected !== null) showTrayTab('piece');
  });
  // The turn of the card is over and the player picked no face: nothing is left to read on the Spell tab.
  gameStore.subscribe(() => {
    if (trayStore.get() !== 'spell' || holdsCard() || wheelPickStore.get() !== null) return;
    showTrayTab(tabAtRest());
  });
};
